import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  LabelFaded,
  ErrorMessage,
} from '../../../ui_reusable/styles/CreateInputUtils.styles'
import {
  FormSelectPicker,
  FormDatePicker,
} from '../../../ui_reusable/StandardSelectPickers'
import {
  RadioProvider,
  StandardRadio,
} from '../../../ui_reusable/StandardRadioButtons'
import {
  CombinedRowTitle,
  DateRangeLabel,
  OrDivider,
  RadioContainer,
} from './styles/BuildModelTimeframeSelector.styles'
import { InputsWrapper, StyledInput } from './styles/ParametersForm.styles'
import {
  Dataset,
  Timeframe,
  OptimizeConfigRequestRoiCalculationPeriod,
} from '@/api/generated/models'
import { StandardInput } from '../../../../components/ui_reusable/StandardInputs'
import {
  type ModelingPreset,
  type DataFrequency,
  type DateAnchor,
  MODELING_PRESETS,
  calculateModelingTimeframe,
  calculateOptimizeTimeframe,
  getDisabledPresets,
  getDefaultOptimizePeriod,
  getMaxOptimizePeriod,
  getMinOptimizePeriod,
  getUnitLabel,
  formatDate,
  inferOptimizePeriodCount,
  inferOptimizeDateAnchor,
} from './utils/logic/optimizeTimeframeUtils'
import { RequiredLabelStar } from '../../../../components/datasets/databook_page/databook/dataset_form/styles/DatasetForm.styles'

const OptimizeTimeframeSelector = ({
  training_timeframe,
  optimize_timeframe,
  dependentDriver,
  modelDataFrequency,
  roiCalculationPeriod,
  onTrainingTimeframeChange,
  onOptimizeTimeframeChange,
  onRoiCalculationPeriodChange,
  error,
}: {
  training_timeframe: Timeframe
  optimize_timeframe: Timeframe
  dependentDriver: Dataset | null
  modelDataFrequency: DataFrequency
  roiCalculationPeriod: OptimizeConfigRequestRoiCalculationPeriod
  onTrainingTimeframeChange: (value: Timeframe) => void
  onOptimizeTimeframeChange: (value: Timeframe) => void
  onRoiCalculationPeriodChange: (
    value: OptimizeConfigRequestRoiCalculationPeriod,
  ) => void
  error: { modeling?: any; optimize?: any }
}) => {
  const [selectionMode, setSelectionMode] = useState<string>('manual')
  const [selectedPreset, setSelectedPreset] = useState<ModelingPreset | null>(
    null,
  )
  const initialDateAnchor = inferOptimizeDateAnchor(
    optimize_timeframe,
    training_timeframe,
  )
  const [optimizePeriodCount, setOptimizePeriodCount] = useState<number | ''>(
    () => {
      if (optimize_timeframe?.from && optimize_timeframe?.to) {
        const existingCount = getMaxOptimizePeriod(
          modelDataFrequency,
          optimize_timeframe.from,
          optimize_timeframe.to,
        )
        return existingCount > 0 ? existingCount : ''
      }
      return ''
    },
  )
  const [optimizePeriodError, setOptimizePeriodError] = useState<string>('')
  const [dateAnchor, setDateAnchor] = useState<DateAnchor>('end_date')
  const [anchorDate, setAnchorDate] = useState<string>('')
  const [hasInteracted, setHasInteracted] = useState<boolean>(() =>
    Boolean(optimize_timeframe?.from && optimize_timeframe?.to),
  )
  const previousFrequencyRef = useRef<DataFrequency | null>(null)

  // Derived driver date range
  const driverStartAt = dependentDriver?.start_at ?? ''
  const driverEndAt = dependentDriver?.end_at ?? ''

  // Calculate which presets are disabled
  const disabledPresets = useMemo(() => {
    if (!driverStartAt || !driverEndAt) return []
    return getDisabledPresets(driverStartAt, driverEndAt)
  }, [driverStartAt, driverEndAt])

  // Calculate max optimize period based on training timeframe and frequency
  const maxOptimizePeriod = useMemo(() => {
    if (!training_timeframe?.from || !training_timeframe?.to) return 0
    return getMaxOptimizePeriod(
      modelDataFrequency,
      training_timeframe.from,
      training_timeframe.to,
    )
  }, [modelDataFrequency, training_timeframe?.from, training_timeframe?.to])

  const minOptimizePeriod = useMemo(
    () => getMinOptimizePeriod(modelDataFrequency),
    [modelDataFrequency],
  )

  const unitLabel = useMemo(
    () => getUnitLabel(modelDataFrequency),
    [modelDataFrequency],
  )

  // On frequency change, drop a user-typed value so the modeling-period
  // default can be reapplied in the new unit. Skip the initial mount so
  // an existing optimize timeframe (edit mode) is not wiped.
  useEffect(() => {
    if (previousFrequencyRef.current === null) {
      previousFrequencyRef.current = modelDataFrequency
      return
    }
    if (previousFrequencyRef.current === modelDataFrequency) return
    previousFrequencyRef.current = modelDataFrequency

    setHasInteracted(false)
    setOptimizePeriodError('')
    setAnchorDate(
      dateAnchor === 'end_date'
        ? (training_timeframe?.to ?? '')
        : (training_timeframe?.from ?? ''),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelDataFrequency])

  // Default anchor date when training timeframe or anchor direction changes
  useEffect(() => {
    if (dateAnchor === 'end_date' && training_timeframe?.to) {
      setAnchorDate(training_timeframe.to)
    } else if (dateAnchor === 'start_date' && training_timeframe?.from) {
      setAnchorDate(training_timeframe.from)
    }
  }, [dateAnchor, training_timeframe?.from, training_timeframe?.to])

  // Fill a default from the modeling period until the user edits the field.
  // Once they have, only revalidate the current count against new bounds.
  useEffect(() => {
    if (
      !hasInteracted &&
      training_timeframe?.from &&
      training_timeframe?.to &&
      maxOptimizePeriod >= minOptimizePeriod &&
      anchorDate
    ) {
      const defaultCount = getDefaultOptimizePeriod(
        modelDataFrequency,
        maxOptimizePeriod,
      )
      validateAndSetOptimizePeriod(defaultCount)
      return
    }
    if (optimizePeriodCount === '') return
    validateAndSetOptimizePeriod(optimizePeriodCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInteracted,
    maxOptimizePeriod,
    minOptimizePeriod,
    modelDataFrequency,
    dateAnchor,
    anchorDate,
    training_timeframe?.from,
    training_timeframe?.to,
  ])

  // Handle radio button mode change
  const handleModeChange = useCallback((value: string) => {
    setSelectionMode(value)
  }, [])

  // Handle manual date change for modeling period
  const handleModelingDateChange = useCallback(
    (dateType: 'from' | 'to', value: string) => {
      if (!value) return

      const dateValue = new Date(value)
      const formattedDate = formatDate(dateValue)

      const newTimeframe = { ...training_timeframe }
      newTimeframe[dateType] = formattedDate

      onTrainingTimeframeChange(newTimeframe)

      // Clear preset since user is manually editing
      setSelectedPreset(null)
    },
    [training_timeframe, onTrainingTimeframeChange],
  )

  // Handle preset selection
  const handlePresetChange = useCallback(
    (value: ModelingPreset | null) => {
      if (!value || !driverEndAt) return

      setSelectedPreset(value)

      const timeframe = calculateModelingTimeframe(value, driverEndAt)
      onTrainingTimeframeChange(timeframe)
    },
    [driverEndAt, onTrainingTimeframeChange],
  )

  // Validate and set optimize period, then compute the timeframe
  const validateAndSetOptimizePeriod = useCallback(
    (count: number | '') => {
      setOptimizePeriodCount(count)

      if (count === '') {
        setOptimizePeriodError('')
        onOptimizeTimeframeChange({ from: '', to: '' })
        return
      }

      const numCount = Number(count)

      if (isNaN(numCount) || !Number.isInteger(numCount)) {
        setOptimizePeriodError('Must be a whole number')
        return
      }

      if (numCount < minOptimizePeriod) {
        setOptimizePeriodError(`Minimum is ${minOptimizePeriod} ${unitLabel}`)
        onOptimizeTimeframeChange({ from: '', to: '' })
        return
      }

      if (maxOptimizePeriod > 0 && numCount > maxOptimizePeriod) {
        setOptimizePeriodError(
          `Cannot exceed ${maxOptimizePeriod} ${unitLabel} (limited by modeling period)`,
        )
        onOptimizeTimeframeChange({ from: '', to: '' })
        return
      }

      if (!anchorDate) {
        setOptimizePeriodError('Select a date first')
        return
      }

      setOptimizePeriodError('')
      const optimizeTimeframe = calculateOptimizeTimeframe(
        numCount,
        anchorDate,
        modelDataFrequency,
        dateAnchor,
      )
      onOptimizeTimeframeChange(optimizeTimeframe)
    },
    [
      minOptimizePeriod,
      maxOptimizePeriod,
      unitLabel,
      anchorDate,
      modelDataFrequency,
      dateAnchor,
      onOptimizeTimeframeChange,
    ],
  )

  // Handle optimize period input change
  const handleOptimizePeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasInteracted(true)
      const rawValue = e.target.value
      if (rawValue === '') {
        validateAndSetOptimizePeriod('')
        return
      }
      const numValue = parseInt(rawValue, 10)
      if (!isNaN(numValue)) {
        validateAndSetOptimizePeriod(numValue)
      }
    },
    [validateAndSetOptimizePeriod],
  )

  // Handle anchor direction change (Start date / End date)
  const handleDateAnchorChange = useCallback(
    (value: string | number | null) => {
      if (!value) return
      setHasInteracted(true)
      setDateAnchor(value as DateAnchor)
    },
    [],
  )

  // Handle anchor date picker change
  const handleAnchorDateChange = useCallback((value: string) => {
    if (!value) return
    setHasInteracted(true)
    const dateValue = new Date(value)
    const formattedDate = formatDate(dateValue)
    setAnchorDate(formattedDate)
  }, [])

  // Check if a date should be disabled for the optimization anchor date picker
  // Only dates within the modeling period (training_timeframe) are allowed
  const isAnchorDateDisabled = useCallback(
    (date: string) => {
      if (!training_timeframe?.from || !training_timeframe?.to) return true

      const checkDate = new Date(date)
      const minDate = new Date(`${training_timeframe.from} 00:00:00`)
      const maxDate = new Date(`${training_timeframe.to} 23:59:00`)

      if (checkDate < minDate) return true
      if (checkDate > maxDate) return true

      return false
    },
    [training_timeframe?.from, training_timeframe?.to],
  )

  // Check if a date should be disabled for the "from" date picker
  const isFromDateDisabled = useCallback(
    (date: string) => {
      if (!driverStartAt || !driverEndAt) return false

      const checkDate = new Date(date)
      const minDate = new Date(`${driverStartAt} 00:00:00`)
      const maxDate = new Date(`${driverEndAt} 23:59:00`)

      // Cannot be before driver start
      if (checkDate < minDate) return true
      // Cannot be after driver end
      if (checkDate > maxDate) return true
      // Cannot be after the current "to" date if set
      if (training_timeframe?.to) {
        const toDate = new Date(training_timeframe.to)
        if (checkDate > toDate) return true
      }

      return false
    },
    [driverStartAt, driverEndAt, training_timeframe?.to],
  )

  // Check if a date should be disabled for the "to" date picker
  const isToDateDisabled = useCallback(
    (date: string) => {
      if (!driverStartAt || !driverEndAt) return false

      const checkDate = new Date(date)
      const minDate = new Date(`${driverStartAt} 00:00:00`)
      const maxDate = new Date(`${driverEndAt} 23:59:00`)

      // Cannot be before driver start
      if (checkDate < minDate) return true
      // Cannot be after driver end
      if (checkDate > maxDate) return true
      // Cannot be before the current "from" date if set
      if (training_timeframe?.from) {
        const fromDate = new Date(training_timeframe.from)
        if (checkDate < fromDate) return true
      }

      return false
    },
    [driverStartAt, driverEndAt, training_timeframe?.from],
  )

  return (
    <RadioProvider
      onChange={handleModeChange}
      initValue={selectionMode}
      group="timeframe-selection"
    >
      <CombinedRowTitle>
        <span>Timeframe:</span>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <RadioContainer>
            <StandardRadio
              size="m"
              value="manual"
              group="timeframe-selection"
            />
          </RadioContainer>
        </StyledInput>
      </CombinedRowTitle>
      <InputsWrapper style={{ gap: '0rem' }}>
        <StyledInput>
          <LabelFaded>
            Modeling period
            <RequiredLabelStar>*</RequiredLabelStar>
          </LabelFaded>
          <FormDatePicker
            format="yyyy-MM-dd"
            shouldDisableDate={(date: string) => isFromDateDisabled(date)}
            value={
              training_timeframe?.from
                ? new Date(training_timeframe.from)
                : null
            }
            onChange={(value: string) =>
              handleModelingDateChange('from', value)
            }
            disabled={!dependentDriver || selectionMode === 'quick'}
            cleanable={false}
            style={{ width: '9rem' }}
          />
          <ErrorMessage></ErrorMessage>
        </StyledInput>
        <DateRangeLabel>to</DateRangeLabel>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <FormDatePicker
            format="yyyy-MM-dd"
            shouldDisableDate={(date: string) => isToDateDisabled(date)}
            value={
              training_timeframe?.to ? new Date(training_timeframe.to) : null
            }
            onChange={(value: string) => handleModelingDateChange('to', value)}
            disabled={!dependentDriver || selectionMode === 'quick'}
            cleanable={false}
            style={{ width: '9rem' }}
          />
          <ErrorMessage></ErrorMessage>
        </StyledInput>
        <OrDivider>or</OrDivider>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <RadioContainer>
            <StandardRadio size="m" value="quick" group="timeframe-selection" />
          </RadioContainer>
          <ErrorMessage></ErrorMessage>
        </StyledInput>
        <StyledInput>
          <LabelFaded>Modeling period (quick select)</LabelFaded>
          <FormSelectPicker
            data={MODELING_PRESETS}
            value={selectedPreset}
            onChange={(value) => handlePresetChange(value as ModelingPreset)}
            placeholder="Custom"
            style={{ width: '15rem' }}
            disabled={!dependentDriver || selectionMode === 'manual'}
            disabledItemValues={disabledPresets}
          />
          <ErrorMessage></ErrorMessage>
        </StyledInput>
      </InputsWrapper>
      <CombinedRowTitle></CombinedRowTitle>
      <InputsWrapper style={{ gap: '0rem' }}>
        <StyledInput>
          <LabelFaded>
            Optimization period
            <RequiredLabelStar>*</RequiredLabelStar>
          </LabelFaded>
          <StandardInput
            type="number"
            min={minOptimizePeriod}
            max={maxOptimizePeriod || undefined}
            value={
              optimizePeriodCount === '' ? '' : String(optimizePeriodCount)
            }
            onChange={handleOptimizePeriodChange}
            disabled={!dependentDriver}
          />
          <ErrorMessage>
            {optimizePeriodError ||
              (hasInteracted ? error?.optimize?.message : '') ||
              ''}
          </ErrorMessage>
        </StyledInput>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <FormSelectPicker
            data={[
              { label: 'Start date', value: 'start_date' },
              { label: 'End date', value: 'end_date' },
            ]}
            value={dateAnchor}
            onChange={handleDateAnchorChange}
            disabled={!dependentDriver}
            style={{ width: '9rem' }}
          />
          <ErrorMessage></ErrorMessage>
        </StyledInput>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <FormDatePicker
            format="yyyy-MM-dd"
            shouldDisableDate={(date: string) => isAnchorDateDisabled(date)}
            value={anchorDate ? new Date(anchorDate) : null}
            onChange={(value: string) => handleAnchorDateChange(value)}
            disabled={
              !dependentDriver ||
              !training_timeframe?.from ||
              !training_timeframe?.to
            }
            cleanable={false}
            style={{ width: '9rem' }}
          />
          <ErrorMessage></ErrorMessage>
        </StyledInput>
        <StyledInput>
          <LabelFaded htmlFor="roi_calculation_period">
            ROI calculation period
          </LabelFaded>
          <FormSelectPicker
            id="roi_calculation_period"
            data={[
              { label: 'Optimization period', value: 'optimize_period' },
              { label: 'Modeling period', value: 'model_period' },
            ]}
            value={roiCalculationPeriod}
            onChange={(value) =>
              onRoiCalculationPeriodChange(
                value as OptimizeConfigRequestRoiCalculationPeriod,
              )
            }
            disabled={!dependentDriver}
            style={{ width: '12rem' }}
          />
          <ErrorMessage></ErrorMessage>
        </StyledInput>
      </InputsWrapper>
      {error?.modeling?.message && (
        <ErrorMessage>{error.modeling.message}</ErrorMessage>
      )}
    </RadioProvider>
  )
}

export default OptimizeTimeframeSelector
